import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {v4 as uuid} from "uuid";
import {StoredIndividualization, Template} from "../types/Individualization";
import {Article} from "../types/Article";
import {fabric} from "fabric";
import {useGlobalContext} from "./globalContext";
import {rescaleFabricObject} from "../helpers/resizeFabricObject";
import {useFetchData} from "../hooks/useFetchData";
import {Font} from "../types/Font";
import {MediaCategory} from "../types/Media";
import {useFetchSingle} from "../hooks/useFetchSingle";

type IndividualizationContextType = {
    currentTab: number
    changeCurrentTab: (tabNumber: number) => void
    individualizationId: string|number
    steps: string[]
    completedSteps: number[]
    errors: string[]
    activeStep: number
    highestStep: number
    changeActiveStep: (stepNumber: number) => void
    article: Article | null
    setArticle: React.Dispatch<React.SetStateAction<Article | null>>
    fetchArticle: (kArtikel?: string|number) => void
    templates: Template[]
    uploadTemplate: (src: string, id?: string|number|null) => void
    removeTemplate: (id: string|number) => void
    changeTemplateName: (id: string|number, name: string ,name_multi?:string ) => void
    changeTemplateSurcharge: (id: string|number, kArtikel: string) => void
    spots: fabric.Object[]
    addSpot: (spot: fabric.Object) => void
    removeSpot: (spot_id: string|number, canvas: fabric.Canvas) => void
    syncSpot: (spot: fabric.Object) => void
    syncHelper: (helper: fabric.Object) => void
    updateSpot: (spot_id: string | number, property: string, value: string) => void
    updateSpotData: (spot_id: string | number, property: string, value: string | number | boolean | number[]) => void
    addEdgeToPolygon: (polygon_id: string | number, template_id: string | number, canvas: fabric.Canvas) => void
    removeEdgeFromPolygon: (polygon_id: string | number, canvas: fabric.Canvas) => void
    helpers: fabric.Object[]
    addHelper: (spot: fabric.Object) => void
    examples: fabric.Object[]
    setExamples:  React.Dispatch<React.SetStateAction<fabric.Object[]>>
    globalSurcharge: string | null
    updateGlobalSurcharge: (kArtikel: string | null) => void
    size: number | null
    updateSize: (wrapper_size: number) => void
    submitIndividualization: () => void
    submitUpdateIndividualization: () => void
    submitDuplicateIndividualization: () => void
    changeStoredIndividualization: (individualization: StoredIndividualization, tab?: number) => void
    fonts: Font[]
    categories: MediaCategory[]
    canvas: fabric.Canvas
    activeSpotId: string|number|null
    activeSpot: fabric.Object|undefined
    changeActiveSpot: (spot_id: string|number|null) => void

    activeTemplateId: string|number|null
    activeTemplate: Template|undefined
    changeActiveTemplate: (template_id: string|number|null) => void

    chosenVariations: Record<string, number[]>
    setChosenVariations: React.Dispatch<React.SetStateAction<Record<string, number[]>>>
}

const IndividualizationContext = createContext<IndividualizationContextType | undefined>(undefined)

export const IndividualizationContextProvider = ({children}: { children: React.ReactNode }): JSX.Element => {
    const {t} = useTranslation(['individualization'])
    const steps = useMemo(() => {
        return [
            t('progress.select_article'),
            t('progress.upload_templates'),
            t('progress.define_areas'),
            t("progress.determine_surcharges")
        ]
    }, [t])

    const {
        data: article,
        fetch: fetchArticle,
        setData: setArticle
    } = useFetchSingle<Article>('articles')

    const [activeStep, setActiveStep] = useState<number>(0)
    const [highestStep, setHighestStep] = useState<number>(0)
    const [completedSteps, setCompletedSteps] = useState<number[]>([])
    const [individualizationId, setIndividualizationId] = useState<number|string>(uuid())
    const [templates, setTemplates] = useState<Template[]>([])
    const [globalSurcharge, setGlobalSurcharge] = useState<string | null>(null)
    const [size, setSize] = useState<number | null>(null)
    const [storedIndividualization, setStoredIndividualization] = useState<StoredIndividualization|null>(null)
    const [currentTab, setCurrentTab] = useState<number>(1)
    const [chosenVariations, setChosenVariations] = useState<Record<string, number[]>>({})

    const filteredVariations = useMemo(() => {
        const filtered: Record<string, number[]> = {}

        Object.keys(chosenVariations).forEach((key) => {
            if (chosenVariations[key].length > 0) {
                filtered[key] = chosenVariations[key]
            }
        });

        return Object.keys(filtered).length > 0 ? filtered : null;
    }, [chosenVariations])

    //DefineAreas
    const [spots, setSpots] = useState<fabric.Object[]>([])
    const [helpers, setHelpers] = useState<fabric.Object[]>([])
    const [examples, setExamples] = useState<fabric.Object[]>([])
    const [activeTemplateId, setActiveTemplateId] = useState<string | number | null>(null)
    const [activeSpotId, setActiveSpotId] = useState<string | number | null>(null)

    const {
        data: fonts,
        fetch: fetchFonts,
    } = useFetchData<Font>('fonts')

    const {
        data: categories,
        fetch: fetchCategories,
    }= useFetchData<MediaCategory>('mediaCategories')



    const canvas = useMemo(() => {
        const c = new fabric.Canvas(document.createElement('canvas'), {
            backgroundColor: 'white',
            selection: false,
            renderOnAddRemove: true,
            preserveObjectStacking: true,
        });

        c.on('selection:cleared', () => {
            changeActiveSpot(null)
        }).on('selection:created', (e) => {
            if (e.selected !== undefined && e.selected.length > 0) {
                if (e.selected[0].data?.type === 'Edge') changeActiveSpot(e.selected[0].data.polygon_id)
                else if (e.selected[0].data?.type === 'Example') changeActiveSpot(e.selected[0].data.spot_id)
                else if (e.selected[0].data?.id) changeActiveSpot(e.selected[0].data.id)
            }
        }).on('selection:updated', (e) => {
            if (e.selected !== undefined && e.selected.length > 0) {
                if (e.selected[0].data?.type === 'Edge') changeActiveSpot(e.selected[0].data.polygon_id)
                else if (e.selected[0].data?.type === 'Example') changeActiveSpot(e.selected[0].data.spot_id)
                else if (e.selected[0].data?.id) changeActiveSpot(e.selected[0].data.id)
            }
        }).on('object:moving', (e) => {
            if (e.target && e.target.data?.type === 'Edge') {
                syncHelper(e.target)
            }
        }).on('object:modified', (e) => {
            if (e.target && e.target.data?.type !== 'Edge' && e.target.data?.type !== 'Example') {
                syncSpot(rescaleFabricObject(e.target))
                removeExample(e.target.data.id)
            }
        })

        return c
    },[])

    const activeSpot: fabric.Object|undefined = useMemo(() => {
        return spots.find((spot) => {
            return spot.data.id === activeSpotId && spot.data.template_id === activeTemplateId
        })
    },[spots, activeSpotId, activeTemplateId])

    const activeTemplate: Template|undefined = useMemo(() => {
        return templates.find((template) => template.id === activeTemplateId)
    }, [activeTemplateId, templates])

    const changeActiveSpot = (spot_id: string | number | null) => {
        setActiveSpotId(spot_id)
    }

    const changeActiveTemplate = (template_id: string | number | null) => {
        setActiveTemplateId(template_id)
    }

    const {api, showToast} = useGlobalContext()

    const changeCurrentTab = (tabNumber: number) => {
        setCurrentTab(tabNumber)
    }

    const changeActiveStep = (stepNumber: number) => {
        if (stepNumber <= steps.length) {
            setActiveStep(stepNumber)
            setHighestStep(prev => {
                if (prev < stepNumber) return stepNumber
                else return prev
            })
        }
    }

    const updateSize = useCallback((wrapper_size: number) => {
        if (wrapper_size !== 0) setSize(wrapper_size)
    }, [])

    const uploadTemplate = (src: string, id: string|number|null = null) => {
        if (!id) {
            const temp: Template = {
                id: uuid(),
                name: '',
                src: src,
                surcharge_kArtikel: null,
                individualization_id: individualizationId,
            }
            setTemplates(prevTemplates => [...prevTemplates, temp])
        } else {
            setTemplates(prevTemplates => {
                return prevTemplates.map((template) => {
                    if (template.id === id) {
                        template.src = src
                    }
                    return template
                })
            })
        }
    }

    const changeTemplateName = (id: string|number, name: string , name_multi?:string) => {
        setTemplates(prevTemplates => {
            return prevTemplates.map((template) => {
                if (template.id === id) {
                    template.name = name
                    template.name_multi = name_multi
                }
                return template
            })
        })
    }

    const changeTemplateSurcharge = (id: string|number, kArtikel: string) => {
        setTemplates(prevTemplates => {
            return prevTemplates.map((template) => {
                if (template.id === id) {
                    template.surcharge_kArtikel = kArtikel
                }
                return template
            })
        })
    }

    const removeTemplate = (id: string|number) => {
        setTemplates(prevTemplates => prevTemplates.filter((template) => template.id !== id))
    }

    const addSpot = (spot: fabric.Object) => {
        setSpots(prevSpots => [...prevSpots, spot])
    }

    const removeSpot = (id: string|number, canvas: fabric.Canvas) => {
        const objects = canvas.getObjects().filter((obj: any) => (obj.data?.id === id || obj.data?.polygon_id === id))

        objects.forEach((obj) => {
            canvas.remove(obj)
        })

        setSpots(prevSpots => prevSpots.filter((spot) => spot.data.id !== id))
        setHelpers(prevHelpers => prevHelpers.filter((helper) => helper.data.polygon_id !== id))
    }

    const syncSpot = useCallback((syncSpot: fabric.Object) => {
        setSpots(prevSpots => [...prevSpots.filter((spot) => spot.data.id !== syncSpot.data.id), syncSpot])
    }, [])

    const removeExample = (spot_id: string|number) => {
        const obj = canvas.getObjects().find((o) => o.data.type === 'Example' && o.data.spot_id === spot_id)
        if (obj) canvas.remove(obj)

        setExamples(prevExamples => prevExamples.filter((example) => example.data.spot_id !== spot_id))
    }

    const updatePolygon = useCallback((polygon_id: string | number, index: number, point: fabric.Point) => {
        setSpots(prevSpots => {
            return prevSpots.map((spot) => {
                if (spot.data.id === polygon_id) {
                    ((spot as fabric.Polygon).points as fabric.Point[])[index] = point

                    const { width, height, left, top } = (spot as fabric.Polygon)._calcDimensions();
                    (spot as fabric.Polygon).set({
                        width,
                        height,
                        pathOffset: new fabric.Point(left + width / 2, top + height / 2),
                        dirty: true
                    }).setCoords();

                    //@ts-ignore
                    (spot as fabric.Polygon)._setPositionDimensions({})
                }
                return spot
            })
        })
    }, [])

    const syncHelper = useCallback((syncHelper: fabric.Object) => {
        setHelpers(prevHelpers => [...prevHelpers.filter((spot) => spot.data.id !== syncHelper.data.id), syncHelper])
        updatePolygon(syncHelper.data.polygon_id, syncHelper.data.index, (syncHelper as fabric.Circle).getCenterPoint())
    }, [updatePolygon])

    const updateSpotData = (spot_id: string | number, property: string, value: string | number | boolean | number[]) => {
        setSpots(prevSpots => {
            return prevSpots.map((spot) => {
                if (spot.data.id === spot_id) {
                    spot.data = {...spot.data, [property]: value}
                }
                return spot
            })
        })
    }

    const updateSpot = (spot_id: string | number, property: string, value: string) => {
        setSpots(prevSpots => {
            return prevSpots.map((spot) => {
                if (spot.data.id === spot_id) {
                    spot.set((property as keyof fabric.Object), parseFloat(value))
                }
                return spot
            })
        })
    }

    const addEdgeToPolygon = (polygon_id: string | number, template_id: string | number, canvas: fabric.Canvas) => {
        setSpots(prevSpots => {
            return prevSpots.map((spot) => {
                if (spot.data.id === polygon_id) {
                    const points = (spot as fabric.Polygon).points as fabric.Point[]
                    if (points.length > 0) {
                        const next = new fabric.Point(
                            (points[0].x + points[points.length - 1].x) / 2,
                            (points[0].y + points[points.length - 1].y) / 2
                        );

                        (spot as fabric.Polygon).points = [...(spot as fabric.Polygon).points as fabric.Point[], next]

                        spot.set({dirty: true});
                        //@ts-ignore
                        (spot as fabric.Polygon)._setPositionDimensions({})

                        const edge = new fabric.Circle({
                            originX: 'center',
                            originY: 'center',
                            left: next.x,
                            top: next.y,
                            radius: 6,
                            angle: 0,
                            fill: '#FC0',
                            centeredScaling: true,
                            selectable: true,
                            transparentCorners: true,
                            hasBorders: false,
                            hasControls: false,
                            hasRotatingPoint: false,
                            data: {
                                id: uuid(),
                                type: 'Edge',
                                index: points.length,
                                polygon_id: polygon_id,
                                template_id: template_id,
                                size: size,
                            },
                            opacity: 1
                        })

                        addHelper(edge)
                        canvas.add(edge)
                    }
                }
                return spot
            })
        })
    }

    const removeEdgeFromPolygon = (polygon_id: string | number, canvas: fabric.Canvas) => {
        setSpots(prevSpots => {
            return prevSpots.map((spot) => {
                if (spot.data.id === polygon_id) {
                    const points = (spot as fabric.Polygon).points as fabric.Point[]

                    if (points.length > 3) {
                        const edges = canvas.getObjects().filter((obj: any) => {
                            return obj.data?.type === 'Edge' && obj.data?.polygon_id === polygon_id
                        }).sort((a: any, b: any) => {
                            return b.data?.index - a.data?.index
                        })

                        if (edges.length > 3) {
                            points.pop();

                            setHelpers(prevHelpers => prevHelpers.filter((helper) => helper.data.id !== edges[0].data.id));
                            canvas.remove(edges[0]);

                            (spot as fabric.Polygon).points = points
                            spot.set({dirty: true});

                            //@ts-ignore
                            (spot as fabric.Polygon)._setPositionDimensions({})
                        }

                    }
                }
                return spot
            })
        })
    }

    const updateGlobalSurcharge = (kArtikel: string | null) => {
        if (kArtikel) setGlobalSurcharge(kArtikel)
        else setGlobalSurcharge(null)
    }

    const addHelper = (helper: fabric.Object) => {
        setHelpers(prevHelpers => [...prevHelpers, helper])
    }

    const submitIndividualization = () => {
        if (!article) return false
        const transferSpots = spots.map((spot) => {
            return ({...spot.data, fabric: JSON.parse(JSON.stringify(spot))})
        })

        const data = {
            id: individualizationId,
            variations: filteredVariations ? JSON.stringify(filteredVariations) : null,
            kArtikel: article.kArtikel,
            templates: JSON.stringify(templates),
            spots: JSON.stringify(transferSpots),
            size,
            surcharge_kArtikel: globalSurcharge
        };

        api.storeIndividualization(data).then((response) => {
            if (response.status === 200 || response.status === 201) {
                showToast(
                    'top-right',
                    'success',
                    'Individualisierung wurde erfolgreich hinzugefügt.'
                )
                setCurrentTab(1)
            } else {
                showToast(
                    'top-right',
                    'error',
                    'Fehler beim Hinzufügen der Individualisierung.'
                )
            }
        })
    }

    const submitUpdateIndividualization = () => {
        if (!article) return false
        const transferSpots = spots.map((spot) => {
            return ({...spot.data, fabric: JSON.parse(JSON.stringify(spot))})
        })

        const data = {
            id: individualizationId,
            kArtikel: article.kArtikel,
            variations: filteredVariations ? JSON.stringify(filteredVariations) : null,
            templates: JSON.stringify(templates),
            spots: JSON.stringify(transferSpots),
            size,
            surcharge_kArtikel: globalSurcharge
        };

        api.updateIndividualization(individualizationId, data).then((response) => {
            if (response.status === 200 || response.status === 201) {
                showToast(
                    'top-right',
                    'success',
                    'Individualisierung wurde erfolgreich aktualisiert.'
                )
                setCurrentTab(1)
            } else {
                showToast(
                    'top-right',
                    'error',
                    'Fehler beim Aktualisieren der Individualisierung.'
                )
            }
        })
    }

    const submitDuplicateIndividualization = () => {
        if (!article) return false
        const transferSpots = spots.map((spot) => {
            return ({...spot.data, fabric: JSON.parse(JSON.stringify(spot))})
        })

        const data = {
            id: individualizationId,
            kArtikel: article.kArtikel,
            variations: filteredVariations ? JSON.stringify(filteredVariations) : null,
            templates: JSON.stringify(templates),
            spots: JSON.stringify(transferSpots),
            size,
            surcharge_kArtikel: globalSurcharge
        };

        api.storeIndividualization(data).then((response) => {
            if (response.status === 200 || response.status === 201) {
                showToast(
                    'top-right',
                    'success',
                    'Individualisierung wurde erfolgreich dupliziert.'
                )
                setCurrentTab(1)
            } else {
                showToast(
                    'top-right',
                    'error',
                    'Fehler beim Duplizieren der Individualisierung.'
                )
            }
        })
    }

    const changeStoredIndividualization = (individualization: StoredIndividualization, tab = 3) => {
        setStoredIndividualization(individualization)
        setCurrentTab(tab)
    }

    const loadIndividualization = useCallback((individualization: StoredIndividualization) => {

        setIndividualizationId(individualization.id)
        setSize(individualization.size)
        setGlobalSurcharge(individualization.surcharge_kArtikel)

        fetchArticle(individualization.kArtikel)

        setChosenVariations(individualization.variations ?? {})

        individualization.templates.forEach((storedTemplate) => {
            const template: Template = {
                id: storedTemplate.id,
                name: storedTemplate.name,
                name_multi: storedTemplate.name_multi,
                src: storedTemplate.src,
                individualization_id: individualization.id,
                surcharge_kArtikel: storedTemplate.surcharge_kArtikel
            }

            setTemplates(prevTemplates => [...prevTemplates, template])

            storedTemplate.spots.forEach((storedSpot) => {
                const {type, ...rest} = storedSpot.fabric
                const style = {
                    opacity: 0.3,
                    fill: '#FC0',
                    borderColor: '#FC0',
                    cornerColor: '#FC0',
                    cornerStyle: ("circle" as "circle"|"rect"|undefined),
                    cornerSize: 12,
                    transparentCorners: false,
                    centeredScaling: true,
                    selectable: true,
                }
                let element: fabric.Circle|fabric.Ellipse|fabric.Rect|fabric.Polygon|null = null

                switch (type) {
                    case 'circle':
                        element = new fabric.Circle({...rest, ...style})
                        break;
                    case 'rect':
                        element = new fabric.Rect({...rest, ...style})
                        break;
                    case 'ellipse':
                        element = new fabric.Ellipse({...rest, ...style})
                        break;
                    case 'polygon':
                        element = new fabric.Polygon([], {
                            ...rest,
                            hasBorders: false,
                            hasControls: false,
                            hasRotatingPoint: false,
                            centeredScaling: true,
                            selectable: false
                        });

                        (rest as fabric.Polygon).points?.forEach((point, index) => {
                            const edge = new fabric.Circle({
                                originX: 'center',
                                originY: 'center',
                                left: point.x,
                                top: point.y,
                                radius: 8,
                                angle: 0,
                                fill: '#FC0',
                                centeredScaling: true,
                                selectable: true,
                                transparentCorners: true,
                                hasBorders: false,
                                hasControls: false,
                                hasRotatingPoint: false,
                                data: {
                                    id: uuid(),
                                    type: 'Edge',
                                    index: index,
                                    template_id: template.id,
                                    polygon_id: storedSpot.id,
                                    size: individualization.size,
                                },
                                opacity: 1
                            })

                            setHelpers(prev => [...prev, edge])
                        })

                        break;
                    default:
                        break;
                }

                const elements: fabric.Object[] = []

                if (element) {
                    element.data = {
                        id: storedSpot.id,
                        template_id: storedSpot.template_id,
                        name: storedSpot.name,
                        name_multi: storedSpot.name_multi ?? '',
                        hint: storedSpot.hint ?? '',
                        hint_multi: storedSpot.hint_multi ?? '',
                        type: storedSpot.type,
                        surcharge_kArtikel: storedSpot.surcharge_kArtikel,
                        size: storedSpot.size,
                        text_color: storedSpot.text_color,
                        multiline: storedSpot.multiline,
                        media_category_filtered: storedSpot.media_category_filtered,
                        grayscale: storedSpot.grayscale,
                        individual_text_color: storedSpot.individual_text_color,
                        individual_font_size: storedSpot.individual_font_size,
                        individual_font_family: storedSpot.individual_font_family,
                        font_size: storedSpot.font_size,
                        font_families: storedSpot.font_families?.toString() ?? '',
                        max_character: storedSpot.max_character,
                        sync_id: storedSpot.sync_id,
                        media_upload: storedSpot.media_upload,
                        media_library: storedSpot.media_library,
                        max_text_fields: storedSpot.max_text_fields,
                        max_image_fields: storedSpot.max_image_fields,
                        media_category_ids: storedSpot.media_category_ids,
                        fixed_user_inputs: storedSpot.fixed_user_inputs,
                    }

                    elements.push(element)
                }

                setSpots(prev => [...prev, ...elements])
            })
        })

    }, [api])

    useEffect(() => {
        fetchFonts()
        fetchCategories()
    },[fetchFonts,fetchCategories])

    useEffect(() => {
        if (storedIndividualization) {
            loadIndividualization(storedIndividualization)
        }
    }, [storedIndividualization, loadIndividualization])

    const clearData = useCallback(() => {
        setIndividualizationId(uuid())
        setSize(null)
        setGlobalSurcharge(null)
        setArticle(null)
        setChosenVariations({})
        setTemplates([])
        setSpots([])
        setHelpers([])
        setStoredIndividualization(null)
        setActiveStep(0)
        setHighestStep(0)
        setCompletedSteps([])

        canvas.clear()
    },[canvas])

    useEffect(() => {
        const completed = []
        if (article) completed.push(0)
        if (templates.length > 0 && templates.filter(t => t.name.length === 0).length === 0) completed.push(1)
        if (spots.length > 0 && spots.filter(s => {
            return !s.data?.name || (s.data.name.length === 0) || (!s.data.type)
        }).length === 0) completed.push(2)
        completed.push(3)

        setCompletedSteps(completed)
    }, [article, templates, spots])

    const errors: string[] = useMemo(() => {
        const errorList: string[] = []

        if (highestStep > 0 && !article) {
            errorList.push(t('error.noArticle'))
        }
        if (highestStep > 1) {
            const noNameTemplateCount = templates.filter(t => t.name.length === 0).length
            if (templates.length === 0) {
                errorList.push(t('error.noTemplate'))
            } else if (noNameTemplateCount > 0) {
                if (noNameTemplateCount === 1) {
                    errorList.push(t('error.noTemplateNameSingle', {number: noNameTemplateCount}))
                } else {
                    errorList.push(t('error.noTemplateNameMulti', {number: noNameTemplateCount}))
                }
            }
        }

        if (highestStep > 2) {
            const noNameSpotCount = spots.filter(s => {
                return (!s.data?.name || s.data.name.length === 0)
            }).length
            const noTypeSpotCount = spots.filter(s => {
                return (!s.data?.type)
            }).length

            if (spots.length === 0) {
                errorList.push(t('error.noSpot'))
            } else {
                if (noNameSpotCount > 0) {
                    if (noNameSpotCount === 1) {
                        errorList.push(t('error.noSpotNameSingle', {number: noNameSpotCount}))
                    } else {
                        errorList.push(t('error.noSpotNameMulti', {number: noNameSpotCount}))
                    }
                }
                if (noTypeSpotCount > 0) {
                    if (noTypeSpotCount === 1) {
                        errorList.push(t('error.noSpotTypeSingle', {number: noTypeSpotCount}))
                    } else {
                        errorList.push(t('error.noSpotTypeMulti', {number: noTypeSpotCount}))
                    }
                }
            }
        }

        return errorList
    },[highestStep, article, spots, templates, t])

    useEffect(() => {
        if (currentTab === 1) {
            clearData()
        }
    }, [currentTab, clearData])

    return (
        <IndividualizationContext.Provider value={{
            currentTab,
            changeCurrentTab,
            individualizationId,
            size,
            updateSize,
            steps,
            fonts,
            categories,
            completedSteps,
            errors,
            activeStep,
            highestStep,
            changeActiveStep,
            templates,
            uploadTemplate,
            changeTemplateName,
            changeTemplateSurcharge,
            removeTemplate,
            article,
            setArticle,
            fetchArticle,
            spots,
            addSpot,
            removeSpot,
            syncSpot,
            syncHelper,
            updateSpot,
            updateSpotData,
            addEdgeToPolygon,
            removeEdgeFromPolygon,
            helpers,
            addHelper,
            examples,
            setExamples,
            globalSurcharge,
            updateGlobalSurcharge,
            submitIndividualization,
            submitUpdateIndividualization,
            submitDuplicateIndividualization,
            changeStoredIndividualization,
            canvas,
            activeSpotId,
            activeSpot,
            changeActiveSpot,
            activeTemplateId,
            activeTemplate,
            changeActiveTemplate,
            chosenVariations,
            setChosenVariations
        }}>
            {children}
        </IndividualizationContext.Provider>
    );
}

export const useIndividualizationContext = () => {
    const data = useContext(IndividualizationContext)

    if (data === undefined) {
        throw new Error('useIndividualizationContext must used within IndividualizationContextProvider')
    }

    return data;
}
